import React from 'react';
import { Box, Container, Typography, Grid, Avatar, Divider, useTheme } from '@mui/material';
import { Scrollbars } from 'react-custom-scrollbars-2';

// Icons
import { Groups as TeamIcon, Flag as MissionIcon, Apps as AppIcon, Update as FutureIcon, ThumbUp as InvitationIcon } from '@mui/icons-material';

// Images
import MissionImage from '../assets/images/mission.png';
import TeamImage from '../assets/images/team.png';
import AppImage from '../assets/images/about_web.png';
import FutureImage from '../assets/images/future_goals.png';
import InvitationImage from '../assets/images/join_us.png';

// Data structure for the sections
const sections = [
    {
        title: "Our Mission",
        icon: <MissionIcon fontSize="large" />,
        image: MissionImage,
        description: "As a team of experienced IT professionals, we are committed to delivering innovative solutions that enhance academic processes and make education more accessible. Our mission is to create intuitive, AI-powered tools that empower educational institutions and foster a love for learning. We aim to revolutionize the way students and educators interact with technology by offering personalized learning experiences and smart educational solutions. Our dedication is to ensure that everyone, regardless of their background, has access to quality education with the help of cutting-edge AI tools.",
    },
    {
        title: "Our Team",
        icon: <TeamIcon fontSize="large" />,
        image: TeamImage,
        description: "We’re a small, dedicated team of experts skilled in AI, software development, UX, and data analytics. With each team member bringing unique strengths, we combine our skills to deliver top-tier solutions, helping academic institutions streamline operations and engage students in meaningful ways. Our diverse team includes specialists in various fields, all working towards a common goal: to simplify and improve the educational process. Together, we’re building a future where technology and education go hand in hand, making learning smarter, faster, and more engaging.",
    },
    {
        title: "About the Web App",
        icon: <AppIcon fontSize="large" />,
        image: AppImage,
        description: "Our web app is designed to bring AI into education, offering features such as automated assistance, customized learning paths, and data-driven insights. Built to simplify processes and enhance learning, this platform supports academic institutions by delivering intelligent, accessible solutions. With Glassyfied, educators can easily create tailored content for their students, and students can receive instant feedback and support to accelerate their learning journey. The app adapts to individual learning styles, providing a truly personalized experience for each user.",
    },
    {
        title: "Our Future Goals",
        icon: <FutureIcon fontSize="large" />,
        image: FutureImage,
        description: "As we continue to develop, our goal is to add more AI-powered features, deepen integrations with other platforms, and expand our reach. We’re committed to listening to our users and staying ahead of trends, ensuring we provide the best possible experience for academic institutions. Our future roadmap includes building more advanced tools for content creation, real-time collaboration, and data analysis, which will allow students and educators to interact in innovative and efficient ways. We also plan to expand our services globally, making AI-driven learning accessible to even more communities.",
    },
    {
        title: "Join Us on This Journey",
        icon: <InvitationIcon fontSize="large" />,
        image: InvitationImage,
        description: "Whether you're an educator, student, or simply interested in the possibilities of AI in education, we welcome you to be part of our journey. Together, let’s shape a future where technology makes education more engaging and accessible. By joining our community, you become a part of a movement that’s transforming education for the better. We believe that collaboration is key to progress, and we invite you to share your feedback, ideas, and experiences with us to help build the next generation of educational technology.",
    },
];


const About = () => {
    const theme = useTheme();

    return (
        <Box sx={{ width: '100%', paddingY: 2 }}>
            <Scrollbars>
                <Container maxWidth="lg">
                    {/* Title */}
                    <Typography variant="h3" align="center" sx={{ fontWeight: 'bold', mb: 6 }}>
                        About Us
                    </Typography>

                    {/* Section Mapping with Alternating Layout */}
                    {sections.map((section, index) => (
                        <div key={index}>
                            <Grid container spacing={4} sx={{ marginBottom: theme.spacing(8) }} direction={index % 2 === 0 ? 'row' : 'row-reverse'}>
                                <Grid item xs={12} md={6}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start', textAlign: 'start' }}>
                                        <Avatar
                                            sx={{
                                                bgcolor: 'primary.dark',
                                                width: 64,
                                                height: 64,
                                                mb: 3,
                                                boxShadow: 3,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            {section.icon}
                                        </Avatar>

                                        <Typography
                                            variant="h5"
                                            sx={{
                                                fontWeight: 600,
                                                mb: 2,
                                                color: 'text.primary',
                                                lineHeight: 1.2, // Enhances readability
                                            }}
                                        >
                                            {section.title}
                                        </Typography>

                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: 'text.secondary',
                                                opacity: 0.85, // Subtle contrast for description
                                                lineHeight: 1.6,
                                                maxWidth: 600, // Controls max width for description
                                                mx: 'auto', // Centers the description
                                            }}
                                        >
                                            {section.description}
                                        </Typography>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Box component="img" src={section.image} alt={section.title} sx={{ width: '100%', borderRadius: 2 }} />
                                </Grid>
                            </Grid>

                            {index < sections.length - 1 && <Divider sx={{ mb: 8 }} />}
                        </div>
                    ))}
                </Container>
            </Scrollbars>
        </Box>
    );
};

export default About;
